export const getInputsOverrides = (theme) => ({
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& input:-webkit-autofill, textarea:-webkit-autofill, select:-webkit-autofill":
            {
              "-webkit-box-shadow": "none",
              "-webkit-text-fill-color": theme.palette.text.primary,
              color: theme.palette.text.primary,
              transition:
                "background-color 5000s ease-in-out 0s, color 5000s ease-in-out 0s",
            },
          "& .MuiOutlinedInput-root": {
            minHeight: "24px", // WCAG 2.2 SC 2.5.8 Level AA — 24px min target size
            "& fieldset": {
              borderColor: theme.palette.divider,
              borderRadius: "6px",
            },
            "&:hover fieldset": {
              borderColor: theme.palette.primary.interactive,
            },
            "&.Mui-focused fieldset": {
              borderColor: theme.palette.primary.interactive,
            },
            // Light resolves to the same #C0392B; dark lightens (was 2.79:1).
            // !important is load-bearing against MUI's own .Mui-error rule.
            "&.Mui-error fieldset": {
              borderColor: `${theme.palette.formMessage.error} !important`,
            },
            "&.Mui-disabled input": {
              cursor: "not-allowed",
              color: theme.palette.text.disabled,
            },
            "&.Mui-disabled": {
              color: theme.palette.text.disabled,
              "-webkit-text-fill-color": theme.palette.text.disabled,
              cursor: "not-allowed",
              "& fieldset": {
                borderColor: theme.palette.action.disabled,
              },
            },
          },
        }),
      },
      variants: [
        {
          props: { size: "onboarding" },
          style: ({ theme }) => ({
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
            "& .MuiInputBase-input": {
              padding: theme.spacingTokens["md-sm"], // 12px
              fontSize: "0.875rem", // 14px
            },
            // Placeholder color now comes from the app-wide MuiInputBase
            // `input` slot override below — no need to restate it here.
          }),
        },
      ],
    },
    MuiSelect: {
        styleOverrides: {
          select: ({ theme }) => ({
            "&:focus": {
              backgroundColor: "transparent",
            },
          }),
        },
      },
    MuiCheckbox: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-focusVisible": {
              outline: `3px solid ${theme.palette.accent?.butterscotch }`,
              outlineOffset: "2px",
            },
            "&.Mui-checked": {
              color: theme.palette.primary.interactive,
            },
          }),
        },
      },
    MuiRadio: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-focusVisible": {
              outline: `3px solid ${theme.palette.accent?.butterscotch }`,
              outlineOffset: "2px",
            },
            "&.Mui-checked": {
              color: theme.palette.primary.interactive,
            },
          }),
        },
      },
    MuiAutocomplete: {
        styleOverrides: {
          popupIndicator: {
            paddingRight: "7px",
          },
        },
      },
    MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.interactive,
              borderWidth: "2px",
            },
            "&.Mui-focusVisible": {
              outline: `3px solid ${theme.palette.accent?.butterscotch }`,
              outlineOffset: "2px",
            },
          }),
        },
      },
    MuiInputBase: {
        styleOverrides: {
          // `-webkit-text-fill-color` PAINTS text in WebKit/Blink and wins
          // over `color` when both are set. It's inherited, so a wrapper
          // that pins it on the base `input` (CssTextField, RewampTextField)
          // leaks it onto `::placeholder` too, unless reset here explicitly —
          // otherwise the placeholder computes the right `color` but still
          // renders in the (dark) inherited fill color.
          input: ({ theme }) => {
            // `--rewampPlaceholder` is an opt-in: a wrapper that sets it
            // (RewampTextField, for the scheme form's darker formText.placeholder)
            // repaints only its own subtree, inheriting through this same rule
            // instead of fighting it on specificity. Unset everywhere else.
            const fill = `var(--rewampPlaceholder, ${theme.palette.neutral.placeholder})`;
            const placeholder = {
              color: fill,
              WebkitTextFillColor: fill,
              opacity: 1,
            };
            return {
              "&::-webkit-input-placeholder": placeholder,
              "&::-moz-placeholder": placeholder,
              "&:-ms-input-placeholder": placeholder,
              "&::-ms-input-placeholder": placeholder,
              "&::placeholder": placeholder,
            };
          },
        },
      },
});
